import React from 'react';
import type { DeepPartial } from 'react-hook-form';
import { HostedButton, HostedButtonDetails, MerchantSettings } from '@client/typings/Common';
import { KrakenRequest } from '@server/typings';
import { transformButtonVariables } from '@client/utils/utils';
import { getButtonVariablesFromMerchantSettings } from '@components/Settings/utils';
import {
  ButtonConfig,
  NoShipping,
  CheckoutButtonTextType,
  SingleButtonTextType,
  CheckoutSingleButtonColorType,
  InventoryType
} from '@client/components/buttons/types';
import { logErrorClient } from '@client/utils/beaverLogger';
import getVersion from '@client/helpers/getVersion';
import { getImageCount } from '@client/utils/image';
import { fptiPageNames, FPTI_GROUP, COMPONENT, PRODUCT, SPACE_KEY } from './fpti';
import { getVariantDetails } from './variants';

export type FlowType =
  | 'paypal'
  | 'merchant'
  | 'unknown'
  | 'preview'
  | 'redirection'
  | 'third-party';
export type FlowVariationType = 'buy_now' | 'add_to_cart';
export type SourceType =
  | 'link'
  | 'qr_code'
  | 'button_code'
  | 'unknown'
  | 'checkout_button'
  | 'single_button'
  | '3_button_code'
  | 'add_to_cart_button'
  | 'view_cart_button';
export type PageName =
  | 'payment'
  | 'paywall'
  | 'order_status'
  | 'deploy_sheet'
  | 'not_found'
  | 'quick_actions'
  | 'account_check_failed'
  | 'confirmation_cart'
  | 'settings'
  | 'merchant_website'
  | 'subscriptions'
  | 'editor';
export type FptiData = {
  eventName: string;
  pageName?: PageName;
  hosted_button_id?: string;
  merchant_id?: string;
  order_id?: string | unknown;
  payment_source?: string;
  selected_payment_method?: string;
  error_type?: string;
  flow_type?: FlowType;
  source_type?: SourceType;
  api_response_code?: number;
  api_duration?: number;
  event?: string;
  flow_variation?: FlowVariationType;
  sold_out?: boolean;
  cust_id?: string;
  [key: string]: unknown;
};

export type MerchantFptiData = {
  eventName: string;
  hosted_button_id?: string;
  merchant_id?: string;
  order_id?: string | unknown;
  payment_source?: string;
  selected_payment_method?: string;
  error_type?: string;
  flow_type?: FlowType;
  source_type?: SourceType;
  api_response_code?: number;
  api_duration?: number;
  [key: string]: unknown;
};

const BASE_TAGS = {
  product: PRODUCT,
  component: COMPONENT,
  pgrp: FPTI_GROUP,
  space_key: SPACE_KEY
};

export const buildBaseTags = (pageName: string | undefined, eventName: string, event?: string) => {
  const page = fptiPageNames[pageName || ''];
  if (event) {
    return {
      ...BASE_TAGS,
      page,
      event_name: eventName,
      e: event,
      event_type: event
    };
  }
  return {
    ...BASE_TAGS,
    page,
    action: eventName
  };
};

export type MerchantSettingOptions = {
  merchantSettings?: MerchantSettings;
};

export const buildAdditionalTags = ({
  buttonDetails,
  sourceType,
  flowType,
  enablePaywall,
  merchantSettingsOptions,
  enablePaywallButtons,
  bnCode,
  trackingId
}: {
  buttonDetails: HostedButtonDetails;
  sourceType: SourceType;
  flowType: FlowType;
  enablePaywall?: boolean;
  merchantSettingsOptions?: MerchantSettingOptions;
  enablePaywallButtons?: boolean;
  bnCode?: string | undefined;
  trackingId?: string;
}) => {
  const merchantSettings = merchantSettingsOptions?.merchantSettings;
  const originalButtonVariables = transformButtonVariables(buttonDetails.link_variables);
  const buttonVariables = getButtonVariablesFromMerchantSettings(
    merchantSettings,
    originalButtonVariables
  );
  const hostedButtonId = buttonDetails.id;
  const {
    business: merchantId,
    shape: buttonShape,
    color: buttonColor,
    ...otherProps
  } = buttonVariables;
  const version = getVersion(enablePaywall, enablePaywallButtons);
  const productImages = buttonVariables?.product_images;
  // Only the first variant could have images
  const primaryVariantOptions = buttonDetails.option_details?.[0]?.selection_details;
  const imageCount = getImageCount(productImages, primaryVariantOptions);
  const variantDetails = getVariantDetails(buttonDetails.option_details, false);
  const enableInventory =
    Array.isArray(buttonDetails?.inventory) && buttonDetails.inventory.length > 0;
  return {
    ...otherProps,
    button_shape: buttonShape,
    button_color: buttonColor,
    merchant_id: merchantId,
    hosted_button_id: hostedButtonId,
    source_type: sourceType,
    flow_type: flowType,
    version,
    image_count: imageCount,
    variant_details: variantDetails,
    enable_inventory: enableInventory,
    ...(enableInventory && {
      inventory_type: buttonDetails?.link_variables?.find((item) => item.name === 'track_inventory')
        ?.value,
      allow_sold_out: buttonDetails?.inventory?.[0]?.allow_over_sell
    }),
    bn_code: bnCode,
    tracking_id: trackingId
  };
};

export type ButtonConfigFptiFlags = {
  shouldSend3ButtonTags?: boolean;
  shouldSendSingleButtonTags?: boolean;
  flowVariation?: FlowVariationType;
};

export const buildButtonConfigTagsV2 = (
  buttonConfig: DeepPartial<ButtonConfig>,
  flags?: ButtonConfigFptiFlags
) => {
  return {
    button_color: buttonConfig.color,
    button_layout: buttonConfig.layout,
    button_shape: buttonConfig.shape,
    button_text: buttonConfig.paypalButtonText,
    button_type: buttonConfig.paymentButtonType,
    component: 'nocodenodeweb',
    is_no_shipping: buttonConfig.isNotCollectShippingAddress,
    variants: buttonConfig.variants,
    label_amount: buttonConfig.amountLabel,
    label_invoice: buttonConfig.invoiceLabel,
    label_note: buttonConfig.memoLabel,
    max_qty: buttonConfig.quantityOption,
    price: buttonConfig.amount,
    pricing_option_selected: buttonConfig.buttonType,
    product_name: buttonConfig.itemName,
    product_id: buttonConfig.productId,
    quantity_option: buttonConfig.isEnableQuantityOption,
    return_url: buttonConfig.customReturnUrl,
    return_url_option: buttonConfig.returnUrlType,
    shipping_config: buttonConfig.shippingPreference,
    shipping_fee: buttonConfig.shipping,
    tax_config: buttonConfig.taxRatePreference,
    tax_rate: buttonConfig.taxRate,
    version: 'v2',
    ...(buttonConfig.enableInventory && {
      allow_sold_out: buttonConfig.allowSoldOut,
      enable_inventory: buttonConfig.enableInventory,
      inventory_type: buttonConfig.isEnableVariants
        ? InventoryType.VARIANT_INVENTORIES
        : InventoryType.PRODUCT_INVENTORIES,
      inventory_alert_qty_non_zero: buttonConfig.isEnableVariants
        ? (buttonConfig.variantInventories || []).some(
            (inventory) => inventory?.alertQuantity && inventory.alertQuantity !== '0'
          )
        : (buttonConfig.productInventories || []).some(
            (inventory) => inventory?.alertQuantity && inventory.alertQuantity !== '0'
          )
    }),
    // 3-button fields, we need to add a ramp check here since these values get
    // pre-populated to default values
    ...(flags?.shouldSend3ButtonTags && {
      button_size: buttonConfig.size,
      button_text_checkout:
        buttonConfig.checkoutButtonTextType === CheckoutButtonTextType.Custom
          ? buttonConfig.checkoutButtonCustomText
          : buttonConfig.checkoutButtonTextType
    }),
    // single button fields
    ...(flags?.shouldSendSingleButtonTags && {
      checkout_button_type: buttonConfig.checkoutButtonType,
      button_size_single: buttonConfig.singleButtonSize,
      button_color_single:
        buttonConfig.singleButtonColorType === CheckoutSingleButtonColorType.Custom
          ? buttonConfig.singleButtonColor
          : buttonConfig.singleButtonColorType,
      button_text_single:
        buttonConfig.singleButtonTextType === SingleButtonTextType.Custom
          ? buttonConfig.singleButtonCustomText
          : buttonConfig.singleButtonTextType
    })
  };
};

// TODO: replace appContext by specific params.
export const buildButtonDetailsTags = (
  hostedButton?: HostedButton,
  merchantSettings?: MerchantSettings
) => {
  const originalButtonVariables = transformButtonVariables(hostedButton?.button_variables);
  const buttonVariables = getButtonVariablesFromMerchantSettings(
    merchantSettings,
    originalButtonVariables
  );
  return {
    button_color: buttonVariables.color,
    button_layout: buttonVariables.layout,
    button_shape: buttonVariables.shape,
    button_text: buttonVariables.button_text,
    button_type: buttonVariables.payment_button_type,
    component: 'nocodenodeweb',
    is_no_shipping: buttonVariables.no_shipping === NoShipping.NoPrompt,
    label_amount: buttonVariables.amount_label,
    label_invoice: buttonVariables.invoice_label,
    label_note: buttonVariables.memo_label,
    max_qty: buttonVariables.quantity_option,
    price: buttonVariables.amount,
    pricing_option_selected: buttonVariables.button_sub_type,
    product_name: buttonVariables.item_name,
    product_id: buttonVariables.product_id,
    quantity_option: buttonVariables.quantity_option !== undefined,
    return_url: buttonVariables.custom_return_url,
    return_url_option: buttonVariables.return_url_type,
    shipping_config: buttonVariables.shipping_preference,
    shipping_fee: buttonVariables.shipping,
    tax_config: buttonVariables.tax_rate_preference,
    tax_rate: buttonVariables.tax_rate,
    version: 'v2',
    // 3-button fields
    button_size: buttonVariables.size,
    button_text_checkout:
      buttonVariables.checkout_button_text_type === CheckoutButtonTextType.Custom
        ? buttonVariables.checkout_button_custom_text
        : buttonVariables.checkout_button_text_type,
    // single button fields
    checkout_button_type: buttonVariables.checkout_button_type,
    button_size_single: buttonVariables.single_button_size,
    button_color_single:
      buttonVariables.single_button_color_type === CheckoutSingleButtonColorType.Custom
        ? buttonVariables.single_button_color
        : buttonVariables.single_button_color_type,
    button_text_single:
      buttonVariables.single_button_text_type === SingleButtonTextType.Custom
        ? buttonVariables.single_button_custom_text
        : buttonVariables.single_button_text_type
  };
};

export type SettingsFptiFlags = {
  isCartButtonSettingEnabled?: boolean;
};

export const buildSettingsTags = (
  merchantSettings: MerchantSettings,
  flags?: SettingsFptiFlags
) => {
  const {
    payment_method_paylater,
    payment_method_venmo,
    payment_method_apple_pay,
    show_business_name,
    show_business_logo,
    homepage_url,
    stacked_button_layout,
    stacked_button_shape,
    stacked_button_size,
    stacked_button_color_type,
    stacked_button_second_button_type,
    stacked_button_text_type,
    stacked_button_custom_text,
    single_button_color_type,
    single_button_shape,
    single_button_size,
    single_button_color_code,
    cart_button_shape,
    cart_button_size,
    cart_button_color_type,
    cart_button_color_code
  } = merchantSettings;

  return {
    payment_method_paylater,
    payment_method_venmo,
    payment_method_apple_pay,
    show_business_name,
    show_business_logo,
    homepage_url,
    stacked_button_layout,
    stacked_button_shape,
    stacked_button_size,
    stacked_button_color_type,
    stacked_button_second_button_type,
    stacked_button_text_type,
    stacked_button_custom_text,
    single_button_shape,
    single_button_size,
    single_button_color_type,
    single_button_color_code,
    ...(flags?.isCartButtonSettingEnabled
      ? {
          cart_button_shape,
          cart_button_size,
          cart_button_color_type,
          cart_button_color_code
        }
      : {})
  };
};

export const recordImpression = (
  fptiData: FptiData,
  onceData?: Partial<FptiData>,
  isFederated?: boolean
) => {
  if (window.PAYPAL?.analytics?.instance) {
    const { eventName, pageName, ...otherProps } = fptiData;
    const data = {
      ...buildBaseTags(pageName, eventName, 'im'),
      ...otherProps
    };
    if (isFederated) {
      // when Federated don't send base data as that pollutes other events fired outside of our app.
      window.PAYPAL.analytics.instance.recordImpression({ eventName }, { ...data, ...onceData });
    } else {
      window.PAYPAL.analytics.instance.recordImpression({ data }, onceData);
    }
  }
};

export const recordClick = (fptiData: FptiData) => {
  if (window.PAYPAL?.analytics?.instance) {
    const { eventName, pageName, ...otherProps } = fptiData;
    const data = {
      ...buildBaseTags(pageName, eventName, 'cl'),
      ...otherProps
    };

    window.PAYPAL.analytics.instance.recordClick({ data });
  }
};

export const recordActivity = (fptiData: FptiData) => {
  if (window.PAYPAL?.analytics?.instance) {
    const { eventName, pageName, ...otherProps } = fptiData;
    const data = {
      ...buildBaseTags(pageName, eventName, 'ac'),
      ...otherProps
    };

    window.PAYPAL.analytics.instance.logActivity({ data });
  }
};

export const recordError = (error: Error, info?: React.ErrorInfo) => {
  const isErrorBoundary = !!info?.componentStack;
  if (window.PAYPAL?.analytics?.instance) {
    const data = {
      ...BASE_TAGS,
      error_description: error.message,
      erpg: isErrorBoundary ? 'error_boundary' : undefined
    };

    window.PAYPAL.analytics.instance.recordError(data);
  }

  if (isErrorBoundary) {
    logErrorClient('ERROR_BOUNDARY_SHOWN', error.message);
  }
};

export const startCPLTracking = (fptiData: FptiData, identifier?: string) => {
  if (window.PAYPAL?.analytics) {
    const { pageName, eventName, event, ...otherProps } = fptiData;
    const data = {
      ...buildBaseTags(pageName, eventName, event),
      ...otherProps
    };
    window.PAYPAL.analytics.startCPLTracking(data, identifier);
  }
};

export const endCPLTracking = (fptiData?: FptiData, identifier?: string) => {
  if (window.PAYPAL?.analytics) {
    let data;
    if (fptiData) {
      const { pageName, eventName, event, ...otherProps } = fptiData;
      data = {
        ...buildBaseTags(pageName, eventName, event),
        ...otherProps
      };
    }
    window.PAYPAL.analytics.endCPLTracking(data, identifier);
  }
};

export const recordServerSide = (
  req: KrakenRequest,
  fptiData: FptiData,
  shouldFlush: boolean = false
) => {
  const { eventName, pageName = '', ...otherProps } = fptiData;
  const base = buildBaseTags(pageName, eventName, 'im');
  if (req.tracking) {
    // Adding this in now as a stop-gap until we figure out how to handle
    // multiple server-side FPTI calls in one request
    if (shouldFlush) {
      req.tracking.flushServerSide({ ...base, ...otherProps }, true);
    } else {
      req.tracking.addServerSideFptiData({ ...base, ...otherProps });
      req.tracking.doAnalytics();
    }
  }
};

export const buildMerchantBaseTags = (eventName: string, event?: string) => {
  const data = {
    product: PRODUCT,
    component: COMPONENT,
    pgrp: FPTI_GROUP,
    space_key: 'SKS09G' // Insightly space key
  };
  if (event) {
    return {
      ...data,
      event_name: eventName,
      e: event,
      event_type: event
    };
  }
  return {
    ...data,
    action: eventName
  };
};

/**
 * Merchant hosted payment button FPTI
 */
export const recordMerchantFPTI = (
  req: KrakenRequest,
  fptiData: MerchantFptiData,
  shouldFlush: boolean = false,
  eventType: string = 'im'
) => {
  const { eventName, ...otherProps } = fptiData;
  const base = buildMerchantBaseTags(eventName, eventType);
  if (req.tracking) {
    if (shouldFlush) {
      req.tracking.flushServerSide({ ...base, ...otherProps }, true);
    } else {
      req.tracking.addServerSideFptiData({ ...base, ...otherProps });
      req.tracking.doAnalytics();
    }
  }
};
